import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';

function Welcome() {
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* Header with Logo and CTA */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <img src="/logo192.png" alt="My Greatest Self" style={styles.logoImage} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button style={styles.journeyButton} onClick={() => navigate('/journey')}>
            The Journey
          </button>
          <button style={styles.signInButton} onClick={() => navigate('/login')}>
            Sign In
          </button>
          <button style={styles.headerButton} onClick={() => navigate('/signup')}>
            Start Free Trial
          </button>
        </div>
      </header>

      <div style={styles.content}>
        {/* Main Title - Big and Centered */}
        <h1 style={styles.mainTitle}>My Greatest Self</h1>
        <p style={styles.tagline}>Transform Your Mind Through Daily Affirmation</p>

        {/* Hero CTA buttons */}
        <div style={styles.heroCta}>
          <button style={styles.ctaButtonHero} onClick={() => navigate('/signup')}>
            Start Free Trial
          </button>
          <button style={styles.journeyButtonHero} onClick={() => navigate('/journey')}>
            The Journey →
          </button>
        </div>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>The Power of Daily Affirmation</h2>
          <p style={styles.paragraph}>
            For over 50 years, Og Mandino's The Greatest Salesman in the World has 
            transformed millions of lives through the practice of reading scrolls—extended 
            affirmations designed to rewire thought patterns and build character through daily repetition.
          </p>
          <p style={styles.paragraph}>
            My Greatest Self brings this timeless practice into the modern era, 
            updated with current neuroscience research on how affirmations actually work.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>How This Works</h2>
          <p style={styles.paragraph}>
            Your brain is designed to change. Neuroscientists call this neuroplasticity—your 
            mind's ability to form new neural pathways throughout your entire life. Every thought 
            you think repeatedly becomes a pathway. Every belief you reinforce becomes your reality.
          </p>
          <p style={styles.paragraph}>
            Research using fMRI brain scans shows that self-affirmation activates the ventromedial 
            prefrontal cortex (VMPFC)—the brain region associated with self-related processing, 
            positive valuation, and reward. When you read these scrolls, you're not just thinking 
            positive thoughts. You're literally restructuring your brain at the neural level.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Your Practice</h2>
          <p style={styles.paragraph}>
            Each scroll is designed to be read three times per day:
          </p>
          <div style={styles.practiceGrid}>
            <div style={styles.practiceCard}>
              <div style={styles.practiceIcon}>🌅 Morning</div>
              <p style={styles.practiceDescription}>
                Upon waking, before you start your day
              </p>
            </div>
            <div style={styles.practiceCard}>
              <div style={styles.practiceIcon}>☀️ Midday</div>
              <p style={styles.practiceDescription}>
                A moment of recalibration
              </p>
            </div>
            <div style={styles.practiceCard}>
              <div style={styles.practiceIcon}>🌙 Evening</div>
              <p style={styles.practiceDescription}>
                Before sleep, when the subconscious is most receptive
              </p>
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>The Scrolls</h2>
          <p style={styles.paragraph}>
            You'll begin with The Scroll of Habit, which teaches your mind the 
            practice of daily reading itself. After establishing the habit, you'll choose 
            which characteristic to develop next: Discipline, Confidence, or Authenticity.
          </p>
        </section>

        <div style={styles.ctaSection}>
          <h2 style={styles.ctaTitle}>Your Transformation Begins Now</h2>
          <p style={styles.ctaParagraph}>
            You're not reading about change. You're actively creating it, starting with your very first reading.
          </p>
          <button style={styles.ctaButton} onClick={() => navigate('/signup')}>
            Start Your 30-Day Free Trial
          </button>
          <p style={styles.ctaSubtext}>
            Full access to all scrolls • No credit card required • Cancel anytime
          </p>
          <p style={styles.loginLink}>
            Already have an account? <span style={styles.link} onClick={() => navigate('/login')}>Log in</span>
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.colors.bgDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    maxWidth: '1200px',
    margin: '0 auto',
    borderBottom: '1px solid ' + theme.colors.bgLight,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
  },
  logoImage: {
    width: '60px',
    height: '60px',
  },
  headerButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8571em 1.7143em',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  journeyButton: {
    background: 'transparent',
    border: 'none',
    color: theme.colors.teal,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0.8571em 0.5714em',
  },
  heroCta: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '60px',
    flexWrap: 'wrap',
  },
  ctaButtonHero: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.875em 2.25em',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  journeyButtonHero: {
    background: 'transparent',
    border: `2px solid ${theme.colors.teal}`,
    borderRadius: '8px',
    padding: '0.875em 2.25em',
    color: theme.colors.teal,
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '60px 20px 40px',
  },
  mainTitle: {
    fontSize: '4rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '16px',
    lineHeight: '1.2',
  },
  tagline: {
    fontSize: '1.5rem',
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: '32px',
    lineHeight: '1.5',
  },
  section: {
    marginBottom: '48px',
  },
  sectionTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: '20px',
  },
  paragraph: {
    fontSize: '1rem',
    lineHeight: '1.8',
    color: theme.colors.textSecondary,
    marginBottom: '16px',
  },
  practiceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    margin: '32px 0',
  },
  practiceCard: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    border: '1px solid ' + theme.colors.bgLight,
  },
  practiceIcon: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: theme.colors.teal,
    marginBottom: '12px',
  },
  practiceDescription: {
    fontSize: '0.875rem',
    color: theme.colors.textMuted,
    lineHeight: '1.5',
  },
  ctaSection: {
    textAlign: 'center',
    marginTop: '60px',
    padding: '40px',
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    border: '1px solid ' + theme.colors.bgLight,
  },
  ctaTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: '16px',
  },
  ctaParagraph: {
    fontSize: '1.125rem',
    color: theme.colors.textSecondary,
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  ctaButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8889em 2.6667em',
    color: 'white',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'transform 0.2s',
  },
  ctaSubtext: {
    fontSize: '0.875rem',
    color: theme.colors.textMuted,
    marginBottom: '16px',
  },
  loginLink: {
    fontSize: '0.875rem',
    color: theme.colors.textSecondary,
    marginTop: '8px',
  },
  link: {
    color: theme.colors.teal,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  signInButton: {
    background: 'transparent',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    padding: '0.8571em 1.7143em',
    color: '#e2e8f0',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
};

export default Welcome;